/**
 * GraphQL Mutations for Complex Process
 * 
 * Centralized mutation definitions
 */

export const ComplexProcessMutations = {
    CREATE_PROCESS: `
        mutation CreateComplexProcess(
            $tenant: String!
            $input: CreateComplexProcessInput!
        ) {
            createComplexProcess(
                tenant: $tenant
                input: $input
            ) {
                id
                name
                domain
                description
                complexity
                avgDuration
                stages {
                    id
                    name
                    stageType
                }
                connections {
                    from
                    to
                    connectionType
                }
                isTemplate
                version
                createdAt
                createdBy
            }
        }
    `,

    UPDATE_PROCESS: `
        mutation UpdateComplexProcess(
            $tenant: String!
            $id: String!
            $input: UpdateComplexProcessInput!
        ) {
            updateComplexProcess(
                tenant: $tenant
                id: $id
                input: $input
            ) {
                id
                name
                domain
                description
                complexity
                avgDuration
                stages {
                    id
                    name
                    stageType
                    actors
                    steps {
                        id
                        name
                    }
                    loops
                }
                connections {
                    id
                    from
                    to
                    connectionType
                    condition
                }
                actors
                externalSystems {
                    id
                    name
                }
                version
                updatedAt
            }
        }
    `,

    DELETE_PROCESS: `
        mutation DeleteComplexProcess(
            $tenant: String!
            $id: String!
        ) {
            deleteComplexProcess(
                tenant: $tenant
                id: $id
            )
        }
    `,

    CLONE_TEMPLATE: `
        mutation CloneProcessTemplate(
            $templateId: String!
            $targetTenant: String!
            $newName: String!
            $customizations: ProcessCustomizationInput
        ) {
            cloneProcessTemplate(
                templateId: $templateId
                targetTenant: $targetTenant
                newName: $newName
                customizations: $customizations
            ) {
                id
                name
                domain
                description
                complexity
                parentId
                version
                createdAt
            }
        }
    `,

    ADD_STAGE: `
        mutation AddStageToProcess(
            $tenant: String!
            $processId: String!
            $stage: ProcessStageInput!
        ) {
            addStageToProcess(
                tenant: $tenant
                processId: $processId
                stage: $stage
            ) {
                id
                stages {
                    id
                    name
                    stageType
                }
            }
        }
    `,

    UPDATE_STAGE: `
        mutation UpdateProcessStage(
            $tenant: String!
            $processId: String!
            $stageId: String!
            $updates: ProcessStageUpdateInput!
        ) {
            updateProcessStage(
                tenant: $tenant
                processId: $processId
                stageId: $stageId
                updates: $updates
            ) {
                id
                stages {
                    id
                    name
                    stageType
                    actors
                    steps {
                        id
                        name
                    }
                }
            }
        }
    `,

    REMOVE_STAGE: `
        mutation RemoveStageFromProcess(
            $tenant: String!
            $processId: String!
            $stageId: String!
        ) {
            removeStageFromProcess(
                tenant: $tenant
                processId: $processId
                stageId: $stageId
            ) {
                id
                stages {
                    id
                    name
                }
                connections {
                    from
                    to
                }
            }
        }
    `,

    ADD_CONNECTION: `
        mutation AddConnectionToProcess(
            $tenant: String!
            $processId: String!
            $connection: ProcessConnectionInput!
        ) {
            addConnectionToProcess(
                tenant: $tenant
                processId: $processId
                connection: $connection
            ) {
                id
                connections {
                    id
                    from
                    to
                    connectionType
                    condition
                }
            }
        }
    `,

    REMOVE_CONNECTION: `
        mutation RemoveConnectionFromProcess(
            $tenant: String!
            $processId: String!
            $connectionId: String!
        ) {
            removeConnectionFromProcess(
                tenant: $tenant
                processId: $processId
                connectionId: $connectionId
            ) {
                id
                connections {
                    id
                    from
                    to
                }
            }
        }
    `,

    PUBLISH_PROCESS: `
        mutation PublishProcess(
            $tenant: String!
            $processId: String!
            $version: String!
            $releaseNotes: String
        ) {
            publishProcess(
                tenant: $tenant
                processId: $processId
                version: $version
                releaseNotes: $releaseNotes
            ) {
                id
                version
                active
                publishedAt
                publishedBy
            }
        }
    `,

    ARCHIVE_PROCESS: `
        mutation ArchiveProcess(
            $tenant: String!
            $processId: String!
            $reason: String
        ) {
            archiveProcess(
                tenant: $tenant
                processId: $processId
                reason: $reason
            ) {
                id
                active
                archivedAt
                archivedBy
                archiveReason
            }
        }
    `,

    RESTORE_PROCESS: `
        mutation RestoreProcess(
            $tenant: String!
            $processId: String!
        ) {
            restoreProcess(
                tenant: $tenant
                processId: $processId
            ) {
                id
                active
                restoredAt
                restoredBy
            }
        }
    `,

    EXECUTE_PROCESS: `
        mutation ExecuteProcess(
            $tenant: String!
            $processId: String!
            $input: ProcessExecutionInput!
        ) {
            executeProcess(
                tenant: $tenant
                processId: $processId
                input: $input
            ) {
                instanceId
                status
                currentStage
                startedAt
                estimatedCompletion
                nextActions {
                    action
                    actor
                    deadline
                }
            }
        }
    `,

    IMPORT_PROCESS: `
        mutation ImportProcess(
            $tenant: String!
            $format: ImportFormat!
            $data: String!
            $options: ImportOptionsInput
        ) {
            importProcess(
                tenant: $tenant
                format: $format
                data: $data
                options: $options
            ) {
                id
                name
                importedAt
                warnings {
                    field
                    message
                }
            }
        }
    `,

    EXPORT_PROCESS: `
        mutation ExportProcess(
            $tenant: String!
            $processId: String!
            $format: ExportFormat!
            $options: ExportOptionsInput
        ) {
            exportProcess(
                tenant: $tenant
                processId: $processId
                format: $format
                options: $options
            ) {
                data
                format
                filename
                size
            }
        }
    `
};

export default ComplexProcessMutations;
